import React, { useEffect, useState } from "react";
import urls from "../../constants";
import { GetListAsync, handleAuthError } from "../../providers/dataProvider";

const emptyForm = {
    currencyName: "",
    shortCode: "",
    conversionRate: "",
    isDefault: false,
    isActive: true,
};

const extractRows = (payload) => {
    if (Array.isArray(payload)) return payload;

    const candidates = [
        payload?.data,
        payload?.items,
        payload?.result,
        payload?.rows,
        payload?.value,
        payload?.data?.data,
        payload?.data?.items,
        payload?.$values,
        payload?.data?.$values,
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
    }

    return [];
};

const CompanyCurrency = ({ companyId }) => {
    const [listData, setListData] = useState([]);
    const [formValues, setFormValues] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [listError, setListError] = useState("");

    const loadCurrencies = async (showLoader = true) => {
        if (!companyId) {
            setListData([]);
            return;
        }
        if (showLoader) setLoading(true);
        try {
            const result = await GetListAsync("generalcurrency", { companyId });
            setListData(extractRows(result?.data));
        } catch (err) {
            setListError(err?.message || "Failed to load currencies");
        } finally {
            if (showLoader) setLoading(false);
        }
    };

    useEffect(() => {
        loadCurrencies(true); // initial load shows loader
    }, [companyId]);

    const updateField = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));

        if (formErrors?.[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const validate = () => {
        const errors = {};

        if (!formValues?.currencyName?.trim()) {
            errors.currencyName = "Currency Name is required";
        }

        if (!formValues?.shortCode?.trim()) {
            errors.shortCode = "Short Code is required";
        }

        if (
            formValues?.conversionRate === "" ||
            formValues?.conversionRate === null ||
            formValues?.conversionRate === undefined
        ) {
            errors.conversionRate = "Conversion Rate is required";
        } else if (isNaN(Number(formValues.conversionRate))) {
            errors.conversionRate = "Conversion Rate must be a valid number";
        } else if (Number(formValues.conversionRate) <= 0) {
            errors.conversionRate = "Conversion Rate must be greater than 0";
        }

        return errors;
    };

    const requestCurrency = async (method, url, payload = null) => {
        const response = await fetch(url, {
            method,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
            body: payload ? JSON.stringify(payload) : undefined,
        });

        if (response.status === 401 || response.status === 403) {
            handleAuthError();
            throw new Error("Unauthorized");
        }

        if (response.status === 204) return {};

        let data = {};
        try {
            data = await response.json();
        } catch (jsonError) {
            data = {};
        }

        if (!response.ok) {
            const message =
                data?.errors?.[0]?.message || data?.message || `Request failed with status ${response.status}`;
            throw new Error(message);
        }

        return data;
    };

    const handleAddOrUpdate = async () => {
        if (!companyId) return;

        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setSaving(true);
        setListError("");
        try {
            const payload = {
                currencyName: formValues.currencyName.trim(),
                shortCode: formValues.shortCode.trim(),
                conversionRate: Number(formValues.conversionRate),
                isDefault: Boolean(formValues.isDefault),
                isActive: Boolean(formValues.isActive),
                companyId,
            };

            if (editingId) {
                await requestCurrency("PATCH", `${urls.GENERAL_CURRENCY_LIST_URL}/${editingId}`, payload);
            } else {
                await requestCurrency("POST", urls.GENERAL_CURRENCY_LIST_URL, payload);
            }

            setFormValues(emptyForm);
            setFormErrors({});
            setEditingId(null);
            await loadCurrencies(false); // silent refresh — no "Loading..." flicker
        } catch (err) {
            setFormErrors({ currencyName: err?.message || "Save failed" });
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setFormValues({
            currencyName: item.currencyName || "",
            shortCode: item.shortCode || "",
            conversionRate: item.conversionRate ?? "",
            isDefault: Boolean(item.isDefault),
            isActive: item.isActive === undefined ? true : Boolean(item.isActive),
        });
        setFormErrors({});
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setFormValues(emptyForm);
        setFormErrors({});
    };

    const handleDelete = async (item) => {
        if (!item?.id) return;
        setSaving(true);
        setListError("");
        try {
            await requestCurrency("DELETE", `${urls.GENERAL_CURRENCY_LIST_URL}/${item.id}`);
            await loadCurrencies(false);
        } catch (err) {
            setListError(err?.message || "Delete failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="card currency-add-sec">
            <div className="card-body">
                {listError && <div className="alert alert-danger mb-3 py-2">{listError}</div>}

                {/* Add / Edit Currency row */}
                <div className="row align-items-start">
                    <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-6 col-sm-12 col-xs-12 mb-2">
                        <div>
                            <label>
                                Currency Name <span className="mandatory">*</span>
                            </label>
                            <input
                                className="form-control"
                                type="text"
                                placeholder="Currency Name"
                                value={formValues.currencyName}
                                onChange={(e) => updateField("currencyName", e.target.value)}
                            />
                            {formErrors?.currencyName && (
                                <p className="form-error">{formErrors.currencyName}</p>
                            )}
                        </div>
                    </div>
                    <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-6 col-sm-12 col-xs-12 mb-2">
                        <div>
                            <label>
                                Short Code <span className="mandatory">*</span>
                            </label>
                            <input
                                className="form-control"
                                type="text"
                                placeholder="Short Code"
                                value={formValues.shortCode}
                                onChange={(e) => updateField("shortCode", e.target.value)}
                            />
                            {formErrors?.shortCode && (
                                <p className="form-error">{formErrors.shortCode}</p>
                            )}
                        </div>
                    </div>
                    <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-6 col-sm-12 col-xs-12 mb-2">
                        <div>
                            <label>
                                Conversion Rate <span className="mandatory">*</span>
                            </label>
                            <input
                                className="form-control"
                                type="number"
                                step="0.0001"
                                placeholder="Conversion Rate"
                                value={formValues.conversionRate}
                                onChange={(e) => updateField("conversionRate", e.target.value)}
                            />
                            {formErrors?.conversionRate && (
                                <p className="form-error">{formErrors.conversionRate}</p>
                            )}
                        </div>
                    </div>
                    <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-6 col-sm-12 col-xs-12 mb-2">
                        <div className="form-check mb-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="companyCurrencyIsDefault"
                                checked={Boolean(formValues.isDefault)}
                                onChange={(e) => updateField("isDefault", e.target.checked)}
                            />
                            <label className="form-check-label mb-0" htmlFor="companyCurrencyIsDefault">
                                Default Currency
                            </label>
                        </div>
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="companyCurrencyIsActive"
                                checked={Boolean(formValues.isActive)}
                                onChange={(e) => updateField("isActive", e.target.checked)}
                            />
                            <label className="form-check-label mb-0" htmlFor="companyCurrencyIsActive">
                                Active
                            </label>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12">
                        <div className="save-btns d-flex justify-content-end">
                            {editingId && (
                                <button
                                    type="button"
                                    className="btn cancel-btn"
                                    onClick={handleCancelEdit}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn save-btn"
                                onClick={handleAddOrUpdate}
                                disabled={saving}
                            >
                                {saving ? "Saving..." : editingId ? "Update" : "Add"}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Currency list table */}
                <div className="currency-table-sec table-sec">
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>Currency</th>
                                <th>Short Code</th>
                                <th>Conversion Rate</th>
                                <th>Default</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center">
                                        Loading...
                                    </td>
                                </tr>
                            ) : listData?.length ? (
                                listData.map((item) => (
                                    <tr key={item.id}>
                                        <td>{item.currencyName}</td>
                                        <td>{item.shortCode}</td>
                                        <td>{item.conversionRate}</td>
                                        <td>{item.isDefault ? "Yes" : "No"}</td>
                                        <td>
                                            <span className={item.isActive ? "status-active" : "status-inactive"}>
                                                {item.isActive ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td>
                                            <i className="far fa-edit" title="Edit" onClick={() => handleEdit(item)}></i>
                                            <i
                                                className="far fa-trash-alt red-tooltip ms-2"
                                                title="Delete"
                                                onClick={() => handleDelete(item)}
                                            ></i>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center">
                                        No currencies added yet
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CompanyCurrency;